<template>
    <div class="smallVesselDisc" @click="click" :class="{ selectable }">
        <img class="discBackground" :src="discImage" />
        <div class="discContents">
            <div v-if="vessel.oemId" class="vesselOEM">{{ vessel.oemId }}</div>
            <h2>{{ vessel.name }}</h2>
            <div class="location" v-if="showLocation && vessel.location">
                {{ vessel.location.line1 }}<br />{{ vessel.location.locality }}, {{ vessel.location.administrativeAreaLevel1 }}
            </div>
            <div class="vesselType">{{ vesselType }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { vesselTypeString, WaterStatusColor, type AccountVesselV1, type AccountVesselV3 } from '@geckoal/gecko-api-client'
import { computed, type PropType } from 'vue'

const props = defineProps({
    vessel: {
        type: Object as PropType<AccountVesselV1 | AccountVesselV3>,
        required: true
    },
    selectable: {
        type: Boolean,
        default: false
    },
    showLocation: {
        type: Boolean,
        required: false
    }
})

const emit = defineEmits(['click'])

const BLUE_IMAGE = '/discs/disc-blue.svg'
const ORANGE_IMAGE = '/discs/disc-orange.svg'
const RED_IMAGE = '/discs/disc-red.svg'
const GRAY_IMAGE = '/discs/disc-gray.svg'

const discImage = computed(() => {
    if (props.vessel?.status?.discElements?.waterStatusColor === WaterStatusColor.Blue) {
        return BLUE_IMAGE
    } else if (props.vessel?.status?.discElements?.waterStatusColor === WaterStatusColor.Orange) {
        return ORANGE_IMAGE
    } else if (props.vessel?.status?.discElements?.waterStatusColor === WaterStatusColor.Red) {
        return RED_IMAGE
    }
    return GRAY_IMAGE
})

const click = () => {
    if (!props.selectable) return
    emit('click', props.vessel)
}

const vesselType = computed(() => {
    return vesselTypeString(props.vessel.type).toUpperCase()
})
</script>

<style lang="scss" scoped>
.smallVesselDisc {
    position: relative;
    display: inline-block;
    width: 180px;
    height: 180px;
    transition: 0.3s transform;
    margin: 10px;

    .discBackground {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 0;
        transition: 0.3s transform;
    }

    .discContents {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        color: white;

        H2 {
            z-index: 1;
            font-size: 20px;
            text-align: center;
        }

        .vesselType {
            z-index: 1;
            background-color: rgba(255, 255, 255, 0.2);
            border-radius: 10px;
            padding: 2px 8px;
            margin-top: 4px;
        }

        .vesselOEM {
            z-index: 1;
        }

        .location {
            text-align: center;
        }
    }
}

.selectable.smallVesselDisc {
    cursor: pointer;

    &:hover {
        transform: scale3d(1.1, 1.1, 1);

        .discBackground {
            transform: rotate(15deg);
        }
    }
}
</style>
